import AppLayout from 'components/layout/AppLayout.jsx';
import ProtectedRoute from 'components/protectedRoute/ProtectedRoute.jsx';
import React from 'react';
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router-dom';
import { FEATURE_NAME, ROLES } from 'utils/constants.js';

import GlobalLocationSelector from 'components/globalLocationSelector/GlobalLocationSelector.jsx';
import AccessPointRecordsHelpDialog from 'features/accessPointRecords/help/HelpDialog';
import AppointmentListHelpDialog from 'features/appointments/help/HelpDialog';
import HomePageHelpDialog from 'features/homePage/help/HelpDialog';
import InventoryHelpDialog from 'features/inventory/help/HelpDialog';
import OrganizationListHelpDialog from 'features/organizationList/help/HelpDialog';
import PeopleAuthorizedListHelpDialog from 'features/peopleAuthorizedList/help/HelpDialog';
import ClientRequestHelpDialog from 'features/RequestTickets/help/HelpDialog';
import AuthorizedOrganizationAndLocationListHelpDialog from 'features/userLocations/help/HelpDialog';
import UserPermissionsHelpDialog from 'features/userPermissions/help/HelpDialog';
import YardCheckHelpDialog from 'features/yardCheck/help/HelpDialog';
import YardCheckHelpDialogDetail from 'features/yardCheck/help/HelpDialogDetail/HelpDialogDetail';
import YardCheckHelpDialogSummary from 'features/yardCheck/help/HelpDialogSummary/HelpDialogSummary';
import { hasAtLeastFullAccessPermissionForFeature, hasAtLeastReadPermissionForFeature } from 'utils/userPermissionHelper';
import FeaturedElement from './FeaturedElement';
import EquipmentAuthorizedHelpDialog from 'features/equipmentAuthorizedList/dialog/EquipmentAuthorizedHelpDialog.jsx';
import ClientEquipmentHelpDialog from 'features/clientEquipment/dialog/ClientEquipmentHelpDialog.jsx';

const Redirect = React.lazy(() => import('pages/Redirect/Redirect.jsx'));
const NoPage = React.lazy(() => import('pages/NoPage.jsx'));
const ControlCenter = React.lazy(() => import('pages/ControlCenter/ControlCenter.jsx'));
const CameraInfo = React.lazy(() => import('pages/CameraInfo/CameraInfo.jsx'));
const MonitoringObject = React.lazy(() => import('pages/MonitoringObjectPage/MonitoringObject.jsx'));
const EmployeePage = React.lazy(() => import('pages/EmployeePage/EmployeePage'));
const PeopleAuthorizedListPage = React.lazy(() => import('pages/PeopleAuthorizedListPage/PeopleAuthorizedListPage'));
const MonitoringElements = React.lazy(() => import('pages/MonitoringElementsPage/MonitoringElements'));
const CompanyAuthorizedListPage = React.lazy(() => import('pages/CompanyAuthorizedListPage/CompanyAuthorizedListPage'));
const OrganizationListPage = React.lazy(() => import('pages/OrganizationListPage/OrganizationListPage'));
const DivisionListPage = React.lazy(() => import('pages/DivisionList/DivisionList'));
const LocationListPage = React.lazy(() => import('pages/LocationListPage/LocationListPage'));
const ListDynamicFormsPage = React.lazy(() => import('pages/DynamicForms/ListDynamicFormsPage/ListDynamicFormsPage'));
const ListSectionTemplatesPage = React.lazy(() => import('pages/DynamicForms/ListSectionTemplatesPage/ListSectionTemplatesPage'));
const ListFieldsTemplatesPage = React.lazy(() => import('pages/DynamicForms/ListFieldsTemplatesPage/ListFieldsTemplatesPage'));
const HomePage = React.lazy(() => import('pages/HomePage/HomePage'));
const DefaultHomePage = React.lazy(() => import('pages/HomePage/DefaultHomePage'));
const RegionsOfInterest = React.lazy(() => import('pages/RegionsOfInterest/RegionsOfInterest'));
const AccessPointRecords = React.lazy(() => import('pages/AccessPointRecords/AccessPointRecords'));
const UserLocationsPage = React.lazy(() => import('pages/UserLocationsPage/UserLocationsPage'));
const UserPermissionsPage = React.lazy(() => import('pages/UserPermissionsPage/UserPermissionsPage'));
const SSOConfigurationPage = React.lazy(() => import('pages/SSOConfigurationPage/SSOConfigurationPage'));
const AudioConfigurationPage = React.lazy(() => import('pages/AudioConfiguration/AudioConfiguration'));
const AppointmentPage = React.lazy(() => import('pages/AppointmentPage/AppointmentPage'));
const ClientPersonnelListPage = React.lazy(() => import('pages/ClientPersonnel/ClientPersonnel'));
const ClientEquipmentListPage = React.lazy(() => import('pages/ClientEquipment/ClientEquipment'));
const EquipmentAuthorizedListPage = React.lazy(() => import('pages/EquipmentAuthorizedListPage/EquipmentAuthorizedListPage'));
const InventoryPage = React.lazy(() => import('pages/InventoryPage/InventoryPage'));
const RequestTickets = React.lazy(() => import('pages/RequestTickets/RequestTickets'));
const RelaysPage = React.lazy(() => import('pages/ReleysPage/Relays'));
const YardCheckPage = React.lazy(() => import('pages/YardCheckPage/YardCheckPage'));
const YardCheckDetailPage = React.lazy(() => import('pages/YardCheckPage/YardCheckDetailPage'));
const YardCheckSummaryPage = React.lazy(() => import('pages/YardCheckPage/YardCheckSummaryPage'));
const MediaProxyPage = React.lazy(() => import('pages/MediaProxy/MediaProxyPage'));
const AiqEventTriggers = React.lazy(() => import('pages/AiqEventTriggers/AiqEventTriggers'));

const HOME_MAP = {
    dashboard: {
        roles: [ROLES.ADMIN, ROLES.TEAM_LEADER, ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.EXTERNAL_USER],
        homeComponent: HomePage,
        routeHandle: {
            topNavMiddleAdornment: <GlobalLocationSelector />,
        },
    },
    default: {
        homeComponent: DefaultHomePage,
    },
};

const getHomeRouteConfig = (user) => {
    const hasMatchingRole = user?.roles?.some((role) => HOME_MAP.dashboard.roles.includes(role));
    if (hasMatchingRole) {
        return HOME_MAP.dashboard;
    } else {
        return HOME_MAP.default;
    }
};

export const createAppRoute = (user, featureFlag, features) => {
    const hasUserRole = (...role) => {
        for (let userRole of role) if (isAllowed(userRole)) return true;
        return false;
        function isAllowed(role) {
            return user?.roles?.includes(role) || user?.roles?.includes(ROLES.ADMIN); //|| user?.roles?.includes(ROLES.USER);
        }
    };
    const homeConfig = getHomeRouteConfig(user);
    const HomeComponent = homeConfig.homeComponent;

    return createBrowserRouter(
        createRoutesFromElements(
            <Route element={<AppLayout user={user} features={features} />}>
                <Route path="/redirect" element={<Redirect user={user} />} />
                <Route element={<ProtectedRoute isAllowed={!!user} redirectPath="/redirect" />}>
                    <Route
                        path="/"
                        element={<HomeComponent />}
                        handle={{
                            topNavMiddleAdornment: homeConfig.routeHandle?.topNavMiddleAdornment,
                            helpDialogComponent: <HomePageHelpDialog />,
                        }}
                    />
                    <Route element={<ProtectedRoute isAllowed={hasUserRole(ROLES.HR)} />}>
                        <Route path="/employees" element={<EmployeePage />} handle={{ pageTitle: 'Employees' }} />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={
                                    hasUserRole(ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER) ||
                                    hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.AUTHORIZED_LIST_PEOPLE, features)
                                }
                            />
                        }
                    >
                        <Route
                            path="/people-authorized-list"
                            element={<PeopleAuthorizedListPage />}
                            handle={{
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                helpDialogComponent: <PeopleAuthorizedListHelpDialog />,
                                pageTitle: 'Authorized People',
                                featureName: FEATURE_NAME.AUTHORIZED_LIST_PEOPLE,
                            }}
                        />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={
                                    hasUserRole(ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER) ||
                                    hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.AUTHORIZED_LIST_ORGANIZATION, features)
                                }
                            />
                        }
                    >
                        <Route
                            path="/company-authorized-list"
                            element={<CompanyAuthorizedListPage />}
                            handle={{
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                helpDialogComponent: <OrganizationListHelpDialog />,
                                pageTitle: 'Authorized Organizations',
                                featureName: FEATURE_NAME.AUTHORIZED_LIST_ORGANIZATION,
                            }}
                        />
                    </Route>

                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={
                                    hasUserRole(ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER) ||
                                    hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.AUTHORIZED_EQUIPMENT, features)
                                }
                            />
                        }
                    >
                        <Route
                            path="/location-equipment"
                            element={
                                <FeaturedElement enabled={featureFlag?.isClientEquipmentEnabled}>
                                    <EquipmentAuthorizedListPage />
                                </FeaturedElement>
                            }
                            handle={{
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                helpDialogComponent: <EquipmentAuthorizedHelpDialog />,
                                pageTitle: 'Authorized Equipment',
                                featureName: FEATURE_NAME.AUTHORIZED_EQUIPMENT,
                            }}
                        />
                    </Route>

                    <Route
                        isAllowed={hasUserRole(ROLES.ADMIN)}
                        path="/sso-configuration"
                        element={<SSOConfigurationPage />}
                        handle={{ pageTitle: 'SSO Configuration' }}
                    />
                    <Route element={<ProtectedRoute isAllowed={hasUserRole(ROLES.HQ_SUPPORT, ROLES.SALES, ROLES.TEAM_LEADER)} />}>
                        <Route path="/organization-list" element={<OrganizationListPage />} handle={{ pageTitle: 'Organizations' }} />
                    </Route>
                    <Route element={<ProtectedRoute isAllowed={hasUserRole(ROLES.HQ_SUPPORT, ROLES.SALES)} />}>
                        <Route path="/location-list" element={<LocationListPage />} handle={{ pageTitle: 'Locations' }} />
                        <Route path="/divison-list" element={<DivisionListPage />} handle={{ pageTitle: 'Divisons' }} />
                    </Route>
                    <Route element={<ProtectedRoute isAllowed={hasUserRole(ROLES.ADMIN, ROLES.TEAM_LEADER, ROLES.AGENT)} />}>
                        <Route path="/client-personnel" element={<ClientPersonnelListPage />} handle={{ pageTitle: 'Personnel' }} />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={hasUserRole(ROLES.ADMIN, ROLES.TEAM_LEADER, ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN)}
                            />
                        }
                    >
                        <Route
                            path="/client-equipment"
                            element={
                                <FeaturedElement enabled={featureFlag?.isClientEquipmentEnabled}>
                                    <ClientEquipmentListPage />
                                </FeaturedElement>
                            }
                            handle={{
                                helpDialogComponent: <ClientEquipmentHelpDialog />,
                                pageTitle: 'Equipment',
                                featureName: FEATURE_NAME.CLIENT_EQUIPMENT,
                            }}
                        />
                    </Route>
                    <Route element={<ProtectedRoute isAllowed={hasUserRole(ROLES.TECH)} />}>
                        <Route path="/monitoring-elements/audio" element={<AudioConfigurationPage />} handle={{ pageTitle: 'Audio' }} />
                        <Route path="/monitoring-elements" element={<MonitoringElements />} handle={{ pageTitle: 'All Elements' }} />
                        <Route path="/monitoring-elements/relay" element={<RelaysPage />} handle={{ pageTitle: 'Relays' }} />
                        <Route path="/control-center" element={<ControlCenter />} handle={{ pageTitle: 'Mission Control' }} />
                        <Route path="/camera-tron" element={<CameraInfo />} handle={{ pageTitle: 'CameraTron' }} />
                        <Route
                            path="/monitoring-object"
                            element={<MonitoringObject />}
                            handle={{ topNavMiddleAdornment: <GlobalLocationSelector />, pageTitle: 'Monitoring Object' }}
                        />
                        <Route path="/incoming-webooks" element={<AiqEventTriggers />} handle={{ pageTitle: 'Incoming Webhooks' }} />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={
                                    hasUserRole(ROLES.ADMIN, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.TEAM_LEADER) ||
                                    hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.ACCESS_POINT_RECORDS, features)
                                }
                            />
                        }
                    >
                        <Route
                            path="/access-point-record-list"
                            element={<AccessPointRecords />}
                            handle={{
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                helpDialogComponent: <AccessPointRecordsHelpDialog />,
                                pageTitle: 'Access Point Records',
                                featureName: FEATURE_NAME.ACCESS_POINT_RECORDS,
                            }}
                        />
                    </Route>
                    <Route element={<ProtectedRoute isAllowed={hasUserRole(ROLES.ADMIN)} />}>
                        <Route path="/media-mtx" element={<MediaProxyPage />} handle={{ pageTitle: 'MediaMTX' }} />
                        <Route
                            path="/regions-of-interest"
                            element={<RegionsOfInterest user={user} />}
                            handle={{ pageTitle: 'Regions Of Interest' }}
                        />
                        <Route
                            path="/authorized-organization-locations"
                            element={
                                <FeaturedElement enabled={!featureFlag?.isUserPermissionEnabled}>
                                    <UserLocationsPage />
                                </FeaturedElement>
                            }
                            handle={{
                                helpDialogComponent: <AuthorizedOrganizationAndLocationListHelpDialog />,
                                pageTitle: 'Authorized Organizations & Locations',
                            }}
                        />
                        <Route
                            path="/dynamic-form-list"
                            element={
                                <FeaturedElement enabled={featureFlag?.isDynamicFormsEnabled}>
                                    <ListDynamicFormsPage />
                                </FeaturedElement>
                            }
                            handle={{ pageTitle: 'Dynamic Forms' }}
                        />
                        <Route
                            path="/section-template-list"
                            element={
                                <FeaturedElement enabled={featureFlag?.isDynamicFormsEnabled}>
                                    <ListSectionTemplatesPage />
                                </FeaturedElement>
                            }
                            handle={{ pageTitle: 'Section Templates' }}
                        />
                        <Route
                            path="/fields-template-list"
                            element={
                                <FeaturedElement enabled={featureFlag?.isDynamicFormsEnabled}>
                                    <ListFieldsTemplatesPage />
                                </FeaturedElement>
                            }
                            handle={{ pageTitle: 'Fields Templates' }}
                        />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={
                                    hasUserRole(ROLES.ADMIN) || hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.USER_AND_PERMISSION, features)
                                }
                            />
                        }
                    >
                        <Route
                            path="/users-permissions"
                            element={
                                <FeaturedElement enabled={featureFlag?.isUserPermissionEnabled}>
                                    <UserPermissionsPage />
                                </FeaturedElement>
                            }
                            handle={{
                                helpDialogComponent: <UserPermissionsHelpDialog roles={user?.roles} />,
                                pageTitle: 'Users and Permissions',
                                featureName: FEATURE_NAME.USER_AND_PERMISSION,
                            }}
                        />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={
                                    hasUserRole(ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.ADMIN, ROLES.TEAM_LEADER) ||
                                    hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.APPOINTMENTS, features)
                                }
                            />
                        }
                    >
                        <Route
                            path="/appointment-list"
                            element={<AppointmentPage />}
                            handle={{
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                helpDialogComponent: <AppointmentListHelpDialog />,
                                pageTitle: 'Appointments',
                                featureName: FEATURE_NAME.APPOINTMENTS,
                            }}
                        />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={
                                    hasUserRole(ROLES.AGENT, ROLES.FACILITY_MANAGER, ROLES.ORGANIZATION_ADMIN, ROLES.ADMIN, ROLES.TEAM_LEADER) ||
                                    hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.INVENTORY, features)
                                }
                            />
                        }
                    >
                        <Route
                            path="/inventory-list"
                            element={<InventoryPage />}
                            handle={{
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                helpDialogComponent: <InventoryHelpDialog />,
                                pageTitle: 'Inventory List',
                                featureName: FEATURE_NAME.INVENTORY,
                            }}
                        />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={
                                    hasUserRole(ROLES.FACILITY_MANAGER) ||
                                    hasUserRole(ROLES.ORGANIZATION_ADMIN) ||
                                    hasUserRole(ROLES.ADMIN) ||
                                    hasUserRole(ROLES.TEAM_LEADER) ||
                                    hasAtLeastReadPermissionForFeature(user, FEATURE_NAME.YARD_CHECK, features)
                                }
                            />
                        }
                    >
                        <Route
                            path="/yard-check-list"
                            element={<YardCheckPage />}
                            handle={{
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                helpDialogComponent: <YardCheckHelpDialog />,
                                pageTitle: 'Inventory Check',
                                featureName: FEATURE_NAME.YARD_CHECK,
                            }}
                        />
                        <Route
                            path="/yard-check-detail/:id"
                            element={<YardCheckDetailPage />}
                            handle={{
                                hideTopNav: true,
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                helpDialogComponent: <YardCheckHelpDialogDetail />,
                                pageTitle: 'Inventory Check',
                                featureName: FEATURE_NAME.YARD_CHECK,
                            }}
                        />
                        <Route
                            path="/yard-check-summary/:id"
                            element={<YardCheckSummaryPage />}
                            handle={{
                                hideTopNav: true,
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                helpDialogComponent: <YardCheckHelpDialogSummary />,
                                pageTitle: 'Inventory Check',
                                featureName: FEATURE_NAME.YARD_CHECK,
                            }}
                        />
                    </Route>
                    <Route
                        element={
                            <ProtectedRoute
                                isAllowed={
                                    hasUserRole(ROLES.FACILITY_MANAGER) ||
                                    hasUserRole(ROLES.ORGANIZATION_ADMIN) ||
                                    hasUserRole(ROLES.ADMIN) ||
                                    hasUserRole(ROLES.TEAM_LEADER) ||
                                    hasAtLeastFullAccessPermissionForFeature(user, FEATURE_NAME.CLIENT_REQUEST, features)
                                }
                            />
                        }
                    >
                        <Route
                            path="/request-tickets"
                            element={<RequestTickets />}
                            handle={{
                                topNavMiddleAdornment: <GlobalLocationSelector />,
                                pageTitle: 'Request Tickets',
                                featureName: FEATURE_NAME.CLIENT_REQUEST,
                                helpDialogComponent: <ClientRequestHelpDialog />,
                            }}
                        />
                    </Route>
                    <Route path="*" element={<NoPage />} />
                </Route>
            </Route>
        )
    );
};
